/**
 * 1.81.0 · Servidor local: qué es este equipo en la red de la sucursal.
 *  · null: como siempre (sincroniza solo con la nube).
 *  · "server": tiene la base de la sucursal, sincroniza con la nube y atiende a los puestos.
 *  · "puesto": no tiene base propia: trabaja con la del servidor; sin servidor no trabaja.
 * Lo que no es secreto va en localStorage (se lee al arrancar, antes de abrir la base). Las claves del
 * vínculo van cifradas con la cuenta de Windows (platform.secrets).
 */
import { lanAvailable } from "./native";
import type { Pairing, PairedDevice } from "./channel";
import { platform } from "@/platform";

export type LanRole = "server" | "puesto";

export interface LanConfig {
  role: LanRole | null;
  /** Puesto: dónde está el servidor (se actualiza solo si cambia de IP y se lo vuelve a encontrar). */
  server?: { host: string; port: number; id: string; name: string } | null;
  /** Servidor: puerto (por defecto 47810) e id propio (no cambia aunque cambie la IP). */
  port?: number;
  serverId?: string;
  /** 1.99.2: servidor: el puesto (id de vínculo) que guarda la copia de lo que falta subir. */
  backupDeviceId?: string | null;
  /**
   * 1.99.11: servidor: la generación de su identidad. Sube cada vez que el puesto de respaldo reemplaza a un
   * servidor roto (mismo id). Se publica en la búsqueda: el servidor que ve otro con su id y una generación más
   * nueva deja de ser servidor; los puestos eligen la más nueva.
   */
  gen?: number;
}

const KEY = "sofia.lan";
const PAIRING = "lan.pairing";
const DEVICES = "lan.devices";

export function readLanConfig(): LanConfig {
  if (!lanAvailable()) return { role: null };
  try {
    const raw = localStorage.getItem(KEY);
    const c = raw ? (JSON.parse(raw) as LanConfig) : null;
    return c && (c.role === "server" || c.role === "puesto" || c.role === null) ? c : { role: null };
  } catch {
    return { role: null };
  }
}

export function writeLanConfig(c: LanConfig) {
  localStorage.setItem(KEY, JSON.stringify(c));
}

/** Rol con el que arrancó la app (cambiarlo pide reiniciar: la base se abre de otra forma). */
export const startupRole: LanRole | null = readLanConfig().role;
export const isPuesto = startupRole === "puesto";
export const isLanServer = startupRole === "server";

export async function loadPairing(): Promise<Pairing | null> {
  const raw = await platform.secrets?.get(PAIRING).catch(() => null);
  try { return raw ? (JSON.parse(raw) as Pairing) : null; } catch { return null; }
}
export async function savePairing(p: Pairing | null) {
  if (p) await platform.secrets?.set(PAIRING, JSON.stringify(p));
  else await platform.secrets?.delete(PAIRING).catch(() => undefined);
}

export async function loadDevices(): Promise<PairedDevice[]> {
  const raw = await platform.secrets?.get(DEVICES).catch(() => null);
  try { return raw ? (JSON.parse(raw) as PairedDevice[]) : []; } catch { return []; }
}
export async function saveDevices(list: PairedDevice[]) {
  await platform.secrets?.set(DEVICES, JSON.stringify(list));
}
